import { formatHours } from '@time-tracking/shared';
import type {
  GeneralActivitiesGridResponse,
  GeneralActivityCell,
  GeneralActivityColumn,
  GeneralActivityMember,
  GeneralActivityTeam,
} from '@time-tracking/shared';

/**
 * Display derivations for the General activities grid. Everything here reads
 * server-computed facts (grants, reasons, whole-team flags, `editable`) and
 * only decides how to show them — no grant rule is re-derived client-side.
 */

/** A heading checkbox's three states. `mixed` renders as native `indeterminate`. */
export const TEAM_CHECK_STATES = ['on', 'off', 'mixed'] as const;
export type TeamCheckState = (typeof TEAM_CHECK_STATES)[number];

/** The id the administrator-only "No team" group uses in cell keys. */
export const NO_TEAM_GROUP_ID = 'no-team';

/** A grid section: a team, or the administrator's "No team" group. */
export type GridGroup =
  | { kind: 'team'; id: string; team: GeneralActivityTeam; members: GeneralActivityMember[] }
  | { kind: 'noTeam'; id: typeof NO_TEAM_GROUP_ID; members: GeneralActivityMember[] };

export function gridGroups(grid: GeneralActivitiesGridResponse): GridGroup[] {
  const groups: GridGroup[] = grid.teams.map((team) => ({
    kind: 'team',
    id: team.teamId,
    team,
    members: team.members,
  }));
  if (grid.noTeam) {
    groups.push({ kind: 'noTeam', id: NO_TEAM_GROUP_ID, members: grid.noTeam.members });
  }
  return groups;
}

/** Stable cell keys — ids only, so focus and pending state survive a refetch. */
export const teamCellKey = (teamId: string, categoryId: string): string =>
  `team:${teamId}:${categoryId}`;
export const personCellKey = (groupId: string, userId: string, categoryId: string): string =>
  `person:${groupId}:${userId}:${categoryId}`;

export function wholeTeamFor(team: GeneralActivityTeam, categoryId: string): boolean {
  return team.settings.find((s) => s.categoryId === categoryId)?.wholeTeam ?? false;
}

export function cellFor(member: GeneralActivityMember, categoryId: string): GeneralActivityCell {
  const cell = member.cells.find((c) => c.categoryId === categoryId);
  // The server sends one cell per column for every member; a missing one is a
  // contract break, not a state to render around.
  if (!cell) throw new Error(`No cell for category ${categoryId}`);
  return cell;
}

/**
 * The heading checkbox's state for one activity.
 *
 * "Fully on" — the only state that offers Off — is whole team on AND everyone
 * on the team granted. Granted-everyone is how "no member has an individual
 * removal" shows in the cells: a removal is exactly an individual choice that
 * leaves the person ungranted. A team built up person by person (whole team
 * off, everyone ticked) is therefore `mixed`, not `on`: clicking it turns the
 * team on, as for any mixed heading, and the server refuses a team off there.
 */
export function teamCheckState(team: GeneralActivityTeam, categoryId: string): TeamCheckState {
  const members = team.members;
  // An empty team's heading is natively disabled (nobody to grant), so its
  // state is moot; 'off' keeps it unticked whatever its whole-team flag says.
  if (members.length === 0) return 'off';
  const granted = members.filter((m) => cellFor(m, categoryId).granted).length;
  const wholeTeam = wholeTeamFor(team, categoryId);
  if (granted === members.length && wholeTeam) return 'on';
  // Whole team on with every current member individually removed is still a
  // team grant — new joiners get the activity, and the server's team off
  // (which only requires whole_team) applies — so it is `mixed`, not `off`:
  // the "+ new joiners" caption must stay, and clicking the heading turns the
  // team back on for everyone (from there a second click offers team off).
  if (granted === 0 && !wholeTeam) return 'off';
  return 'mixed';
}

/**
 * The "Also on" pill: its visible (and, at long team names, ellipsis-truncated
 * — divergence D12) label, and the full text for its title and screen readers.
 * The full text matches the handoff's title — each other team followed by its
 * lead in brackets — where the lead is known: `otherTeams` carries names only,
 * so a lead is shown for teams in this response (the viewer's own teams, or
 * every team for an administrator) and left off for the rest.
 */
export function alsoOnText(
  otherTeams: readonly string[],
  leadByTeamName: ReadonlyMap<string, string | null>,
): { label: string; full: string } {
  const full = otherTeams
    .map((name) => {
      const lead = leadByTeamName.get(name);
      return lead ? `${name} (${lead})` : name;
    })
    .join(', ');
  return { label: `Also on ${otherTeams.join(', ')}`, full: `Also on ${full}` };
}

/** "6h" — minutes through the shared formatter, never a local rounding. */
export const hoursLabel = (minutes: number): string => `${formatHours(minutes)}h`;

/** How the person sub-line is coloured: Bluebonnet "via", or amber "row stays". */
export type SubLineTone = 'via' | 'stays';

/**
 * The teams a granted person also has the activity through, when the grid shows
 * a "via" line for them; null otherwise. The visible sub-line and the checkbox
 * description both read this, so the description always carries what the
 * (often truncated) line shows.
 */
function shownViaTeams(
  member: GeneralActivityMember,
  cell: GeneralActivityCell,
): readonly string[] | null {
  return cell.granted && member.otherTeams.length > 0 && cell.viaTeams.length > 0
    ? cell.viaTeams
    : null;
}

/**
 * The fixed 13px line under a person's checkbox: "via {Team}" when granted
 * through one of their teams, "via {n} teams" through several (real team names
 * are long, so naming two would truncate the second away — D12), "{h}h · row
 * stays" when ungranted but holding hours (or "note · row stays" for a
 * note-only row), otherwise nothing. `title` is the full text for the tooltip.
 */
export function personSubLine(
  member: GeneralActivityMember,
  cell: GeneralActivityCell,
): { text: string; title: string; tone: SubLineTone } | null {
  if (!cell.granted && cell.hasHoursOrNote) {
    const minutes = cell.minutes ?? 0;
    const text = `${minutes > 0 ? hoursLabel(minutes) : 'note'} · row stays`;
    return { text, title: text, tone: 'stays' };
  }
  const via = shownViaTeams(member, cell);
  if (via) {
    const title = `via ${via.join(' + ')}`;
    return { text: via.length > 1 ? `via ${via.length} teams` : title, title, tone: 'via' };
  }
  return null;
}

export const PERSON_READ_ONLY_REASON =
  'Read only: only an approver of one of their teams, or an administrator, can change this.';
export const TEAM_READ_ONLY_REASON =
  "Read only: only this team's approvers, or an administrator, can change this.";

/**
 * The person checkbox's accessible description (its name is "{Activity} for
 * {Person}"): the sub-line spelled out, the hours a still-granted row holds
 * (which have no visible pill), who made an individual choice, and why the
 * cell is read-only.
 */
export function personCellDescription(
  member: GeneralActivityMember,
  cell: GeneralActivityCell,
): string {
  const parts: string[] = [];
  const minutes = cell.minutes ?? 0;
  if (!cell.granted && cell.hasHoursOrNote) {
    parts.push(
      `${minutes > 0 ? `${hoursLabel(minutes)} this period` : 'Note this period'}, no longer given, row stays`,
    );
  } else if (cell.granted && cell.hasHoursOrNote && minutes > 0) {
    parts.push(`${hoursLabel(minutes)} this period`);
  }
  const via = shownViaTeams(member, cell);
  if (via) parts.push(`via ${via.join(' and ')}`);
  if (cell.reason === 'individual') {
    const verb = cell.granted ? 'added' : 'removed';
    parts.push(cell.setBy ? `${verb} individually by ${cell.setBy}` : `${verb} individually`);
  }
  if (!cell.editable) parts.push(PERSON_READ_ONLY_REASON);
  return parts.join('. ');
}

/** Distinct visible people with the activity, of distinct visible people. */
export function columnCoverage(
  groups: readonly GridGroup[],
  activity: GeneralActivityColumn,
  memberFilter: (m: GeneralActivityMember) => boolean,
): { have: number; total: number } {
  const seen = new Map<string, boolean>();
  for (const g of groups) {
    for (const m of g.members) {
      if (!memberFilter(m) || seen.has(m.userId)) continue;
      seen.set(m.userId, cellFor(m, activity.categoryId).granted);
    }
  }
  return { have: [...seen.values()].filter(Boolean).length, total: seen.size };
}

export const peopleCount = (n: number): string => `${n} ${n === 1 ? 'person' : 'people'}`;
